import { useState } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { useMsal } from '@azure/msal-react';
import useSensorStore from '../store/sensorStore';
import { msalConfig } from '../auth/authConfig';
import { errorHandler } from '../utils/helpers';
import { acquireToken } from '../utils/auth';

export interface FetchSensorsParams {
  page: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  sensorId?: string;
  statusId?: number;
  clientId?: number;
  siteId?: number;
  vendorId?: number;
  sensorModelId?: number;
  useCaseId?: number;
}

interface SensorResponsePagedResponse {
  items: any[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface FetchUser {
  token: string | null;
  params: FetchSensorsParams;
  updateSensorsList: (data: any[]) => void;
  updateSensorsPage: (data: { totalCount: number; page: number; pageSize: number; totalPages: number }) => void;
  setError: (data: any) => any;
  setLoading: (data: any) => any;
};

const fetchData = async ({
    token,
    params,
    updateSensorsList,
    updateSensorsPage,
    setError,
    setLoading,
  }: FetchUser) => {
  setLoading(true);
  try {
    const path = `${window.__env.REACT_APP_API_URL || process.env.REACT_APP_API_URL}/Sensor/paged`
    const response = await axios.get<SensorResponsePagedResponse>(path, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json, text/plain, */*',
      },
      params: {
        Page: params.page,
        SortBy: params.sortBy,
        SortDirection: params.sortDirection,
        SensorId: params.sensorId,
        StatusId: params.statusId,
        ClientId: params.clientId,
        SiteId: params.siteId,
        VendorId: params.vendorId,
        SensorModelId: params.sensorModelId,
        UseCaseId: params.useCaseId,
      },
    });
    const { items, totalCount, page, pageSize, totalPages } = response.data;
    updateSensorsList(items);
    updateSensorsPage({ totalCount, page, pageSize, totalPages });
  } catch (error) {
    errorHandler(error, setError);
  } finally {
    setLoading(false);
  }
};

interface FetchSensors {
  fetchSensors: (params: FetchSensorsParams) => void;
  loading: boolean;
  error: Error | null;
};

const useFetchSensors = (): FetchSensors => {
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const { updateSensorsList, updateSensorsPage } = useSensorStore();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSensors = async (params: FetchSensorsParams) => {
    if (accounts.length > 0) {
      const accessTokenRequest = {
        scopes: [`${msalConfig.auth.clientId}/.default`],
        account: accounts[0],
      };
      try {
        const accessToken = await acquireToken(instance, accessTokenRequest);
        fetchData({
          token: accessToken,
          params,
          updateSensorsList,
          updateSensorsPage,
          setError,
          setLoading,
        });
      } catch (error) {
        errorHandler(error, setError);
      }
    } else {
      navigate('/sign-in');
    }
  }

  return { fetchSensors, loading, error };
};

export default useFetchSensors;
